import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Box, Text, Group, RingProgress, SimpleGrid, Skeleton, Stack, Badge, Anchor, Divider, ScrollArea } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { BarChart } from '@mantine/charts'
import {
  IconDeviceDesktop, IconTool, IconAlertTriangle,
  IconPackage, IconKey, IconClipboardCheck, IconBuilding,
  IconClock, IconArrowsExchange, IconActivity,
} from '@tabler/icons-react'
import {
  getDashboard, getInventorySummary, getLicenseSummary,
  getAllBorrows, getRecentActivity,
} from '../api/client'
import { useAuth } from '../hooks/useAuth'

const STATUS_COLOR: Record<string, string> = {
  AVAILABLE: '#0d9488', BORROWED: '#6366f1', ASSIGNED: '#3b82f6', INSTALLED: '#06b6d4',
  REPAIR: '#f59e0b', DAMAGED: '#ef4444', LOST: '#8b5cf6', WRITE_OFF: '#94a3b8',
}

// ── Custom SVG icons for Dashboard Asset cards ────────────────────────────────
const SvgTotalAssets = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="8" width="40" height="32" rx="4" fill={color + '22'} stroke={color} strokeWidth="2.5"/>
    <rect x="10" y="14" width="10" height="10" rx="2" fill={color} opacity="0.85"/>
    <rect x="26" y="14" width="12" height="3" rx="1.5" fill={color} opacity="0.6"/>
    <rect x="26" y="20" width="8" height="3" rx="1.5" fill={color} opacity="0.4"/>
    <rect x="10" y="28" width="28" height="3" rx="1.5" fill={color} opacity="0.5"/>
    <rect x="10" y="33" width="20" height="3" rx="1.5" fill={color} opacity="0.35"/>
    <circle cx="38" cy="36" r="7" fill={color}/>
    <path d="M35 36l2 2 4-4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const SvgAvailable = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="6" width="24" height="30" rx="3" fill={color + '18'} stroke={color} strokeWidth="2.5"/>
    <rect x="14" y="13" width="12" height="3" rx="1.5" fill={color} opacity="0.6"/>
    <rect x="14" y="20" width="8" height="3" rx="1.5" fill={color} opacity="0.4"/>
    <rect x="14" y="27" width="10" height="3" rx="1.5" fill={color} opacity="0.4"/>
    <circle cx="36" cy="34" r="9" fill={color}/>
    <path d="M31.5 34l3 3 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const SvgWarranty = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="26" r="16" fill={color + '18'} stroke={color} strokeWidth="2.5"/>
    <path d="M24 16v10l6 4" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 8h16M24 6v4" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="36" cy="13" r="6" fill={color}/>
    <path d="M33 13l2 2 4-3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const SvgMaintenance = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 8c-5 0-9 4-9 9 0 1.2.2 2.4.6 3.4L10 34a3 3 0 1 0 4 4l14-13.6c1 .4 2.2.6 3.4.6 5 0 9-4 9-9 0-1-.2-2-.5-2.9l-5 5-3-3 5-5c-.9-.3-1.9-.5-2.9-.5z" fill={color + '22'} stroke={color} strokeWidth="2.2" strokeLinejoin="round"/>
    <circle cx="12.5" cy="35.5" r="2" fill={color} opacity="0.7"/>
  </svg>
)

const SvgInventory = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 5L42 15v18L24 43 6 33V15L24 5z" fill={color + '18'} stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>
    <path d="M6 15l18 10M42 15L24 25M24 25v18" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <path d="M15 10l18 10" stroke={color} strokeWidth="1.5" opacity="0.5" strokeLinecap="round"/>
  </svg>
)

const SvgLowStock = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 5L42 15v18L24 43 6 33V15L24 5z" fill={color + '15'} stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>
    <path d="M6 15l18 10M42 15L24 25M24 25v18" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <circle cx="24" cy="20" r="9" fill={color}/>
    <path d="M24 13v7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="24" cy="23.5" r="1.2" fill="#fff"/>
  </svg>
)

const SvgLicense = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="8" width="28" height="36" rx="3" fill={color + '18'} stroke={color} strokeWidth="2.5"/>
    <rect x="12" y="16" width="16" height="3" rx="1.5" fill={color} opacity="0.6"/>
    <rect x="12" y="22" width="12" height="3" rx="1.5" fill={color} opacity="0.4"/>
    <rect x="12" y="28" width="14" height="3" rx="1.5" fill={color} opacity="0.4"/>
    <circle cx="35" cy="14" r="8" fill={color}/>
    <path d="M31 14l2.5 2.5L39 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const SvgExpiring = ({ color }: { color: string }) => (
  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="36" height="32" rx="3" fill={color + '15'} stroke={color} strokeWidth="2.5"/>
    <path d="M6 18h36" stroke={color} strokeWidth="2" opacity="0.5"/>
    <rect x="14" y="6" width="4" height="8" rx="2" fill={color}/>
    <rect x="30" y="6" width="4" height="8" rx="2" fill={color}/>
    <circle cx="24" cy="31" r="9" fill={color}/>
    <path d="M24 25v6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="24" cy="34.5" r="1.2" fill="#fff"/>
  </svg>
)

// Map ชื่อ icon เดิม → component SVG ใหม่
const ICON_MAP: Record<string, React.ComponentType<{ color: string }>> = {
  IconDeviceDesktop: SvgAvailable,
  IconTool: SvgMaintenance,
  IconAlertTriangle: SvgWarranty,
  IconPackage: SvgInventory,
  IconKey: SvgLicense,
}

function StatCard({ label, value, icon: Icon, color, sub, onClick, svgIcon: SvgIcon }: {
  label: string; value: any; icon: any; color: string; sub?: string; onClick?: () => void; svgIcon?: React.ComponentType<{ color: string }>
}) {
  const IconComponent = SvgIcon ?? ICON_MAP[Icon?.displayName ?? ''] ?? null
  return (
    <Box onClick={onClick}
      style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 16px 16px', cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = color }}
      onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = '#e2e8f0' }}
    >
      <Box style={{ width: 64, height: 64, borderRadius: '50%', background: color + '12', border: `2px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {IconComponent ? <IconComponent color={color} /> : <Icon size={28} color={color} />}
      </Box>
      {value !== undefined ? (
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 700, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>
          {value}
        </Text>
      ) : (
        <Skeleton width={60} height={32} />
      )}
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, letterSpacing: '0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
        {label}
      </Text>
      {sub && <Text size="xs" c="dimmed" ta="center">{sub}</Text>}
    </Box>
  )
}

export default function DashboardPage() {
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { roles, departments: userDepts, fullName, username } = useAuth()
  const isAdminLevel = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN') || roles.includes('ASSET_MANAGER')
  // USER/AUDITOR: ถ้ามี dept = scoped, ถ้าไม่มี dept = เห็นทุกแผนก
  const scopedDepts = !isAdminLevel ? (userDepts ?? []) : []
  const isScopedToDepts = scopedDepts.length > 0

  const [assetData, setAssetData]       = useState<any>(null)
  const [invSummary, setInvSummary]     = useState<any>(null)
  const [licSummary, setLicSummary]     = useState<any>(null)
  const [overdueData, setOverdueData]   = useState<{ borrows: number; warranty: number; license: number } | null>(null)
  const [activity, setActivity]         = useState<any[]>([])

  useEffect(() => {
    getDashboard().then(r => setAssetData(r.data)).catch(() => {})
    getInventorySummary().then(r => setInvSummary(r.data)).catch(() => {})
    getLicenseSummary().then(r => setLicSummary(r.data)).catch(() => {})
    getAllBorrows().then(r => {
      const borrows: any[] = r.data ?? []
      const today = new Date()
      const overdue = borrows.filter(b =>
        b.status === 'APPROVED' && b.dueDate && new Date(b.dueDate) < today
      ).length
      setOverdueData(prev => ({ borrows: overdue, warranty: prev?.warranty ?? 0, license: prev?.license ?? 0 }))
    }).catch(() => {})
    getRecentActivity(15).then(r => setActivity(r.data ?? [])).catch(() => {})
  }, [])

  const barData = assetData
    ? Object.entries(assetData.byStatus).map(([name, value]) => ({ name, value }))
    : []

  const categoryData = assetData
    ? Object.entries(assetData.byCategory).map(([name, value]) => ({ name, value }))
    : []

  const ringData = assetData
    ? Object.entries(assetData.byStatus)
        .filter(([, v]) => (v as number) > 0)
        .map(([k, v]) => ({ value: v as number, color: STATUS_COLOR[k] ?? '#cbd5e1', tooltip: k }))
    : []

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb={isMobile ? 'md' : 'xl'}>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('dashboard.title','แดชบอร์ดทรัพย์สิน')}
        </Text>
        {/* Dept badge / scope info */}
        <Group gap={8} mt={4} align="center" wrap="wrap">
          {isScopedToDepts ? (
            <>
              <Text size="sm" c="dimmed">{t('dashboard.viewingDeptOnly','แสดงข้อมูล')}</Text>
              {scopedDepts.map(dept => (
                <Badge key={dept}
                  leftSection={<IconBuilding size={11} />}
                  color="teal" variant="light" size="md"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  {dept}
                </Badge>
              ))}
              <Text size="sm" c="dimmed">{t('dashboard.deptOnly','เท่านั้น')}</Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">{t('dashboard.subtitleAll','ภาพรวมระบบ — ทุกแผนก')}</Text>
          )}
        </Group>
      </Box>

      {/* ── Assets row ── */}
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>
        {t('dashboard.sectionAssets','Assets')}
      </Text>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
        <StatCard label={t("dashboard.totalAssets")}        value={assetData?.totalAssets}         icon={IconDeviceDesktop} svgIcon={SvgTotalAssets} color="#0d9488" onClick={() => navigate('/assets')} />
        <StatCard label={t("dashboard.available")}           value={assetData?.byStatus?.AVAILABLE}  icon={IconDeviceDesktop} svgIcon={SvgAvailable}   color="#3b82f6" onClick={() => navigate('/assets')} />
        <StatCard label={t("dashboard.warrantyExpiring")}   value={assetData?.warrantyExpiring}     icon={IconAlertTriangle} svgIcon={SvgWarranty}    color="#f59e0b" sub={t("common.next30days", "Next 30 days")} onClick={() => navigate('/assets')} />
        <StatCard label={t("dashboard.pendingMaintenance")} value={assetData?.pendingMaintenance}   icon={IconTool}          svgIcon={SvgMaintenance} color="#ef4444" onClick={() => navigate('/maintenance')} />
      </SimpleGrid>

      {/* ── Inventory + License row ── */}
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>
        {t('dashboard.sectionInventory','Inventory & Licenses')}
      </Text>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
        <StatCard label={t("dashboard.inventoryItems")}   value={invSummary?.totalItems}      icon={IconPackage}       svgIcon={SvgInventory} color="#6366f1" onClick={() => navigate('/inventory')} />
        <StatCard label={t("dashboard.lowStock")}         value={invSummary?.lowStockCount}   icon={IconAlertTriangle} svgIcon={SvgLowStock}  color="#f59e0b" sub={t("common.atOrBelowMin", "At or below min")} onClick={() => navigate('/inventory')} />
        <StatCard label={t("dashboard.activeLicenses")}   value={licSummary?.active}          icon={IconKey}           svgIcon={SvgLicense}   color="#0d9488" onClick={() => navigate('/licenses')} />
        <StatCard label={t("dashboard.expiringSoon")}     value={licSummary?.expiring}        icon={IconAlertTriangle} svgIcon={SvgExpiring}  color="#ef4444" sub={t("common.within30days", "Within 30 days")} onClick={() => navigate('/licenses')} />
      </SimpleGrid>

      <Grid gutter={isMobile ? 'md' : 'xl'}>
        {/* Bar chart — by status */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Box style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '16px' : '20px 24px', border: '1px solid #e2e8f0' }}>
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>{t("dashboard.byStatus")}</Text>
            {assetData ? (
              <BarChart
                h={isMobile ? 180 : 220}
                data={barData}
                dataKey="name"
                series={[{ name: 'value', label: 'Count', color: '#0d9488' }]}
                tickLine="none"
                gridAxis="y"
              />
            ) : <Skeleton height={isMobile ? 180 : 220} />}
          </Box>
        </Grid.Col>

        {/* Ring chart */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Box style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '16px' : '20px 24px', border: '1px solid #e2e8f0' }}>
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>{t("dashboard.statusDistribution","Status Distribution")}</Text>
            {assetData ? (
              <Group justify={isMobile ? 'center' : 'space-between'} align="flex-start" wrap={isMobile ? 'wrap' : 'nowrap'}>
                <RingProgress
                  size={isMobile ? 130 : 150} thickness={16}
                  sections={ringData}
                  label={
                    <Text ta="center" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: isSmall ? 18 : 22, color: '#0f172a' }}>
                      {assetData.totalAssets}
                    </Text>
                  }
                />
                <Stack gap={6} mt={4} style={{ width: isMobile ? '100%' : 'auto' }}>
                  {Object.entries(assetData.byStatus)
                    .filter(([, v]) => (v as number) > 0)
                    .map(([k, v]) => (
                      <Group key={k} gap={6}>
                        <Box style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[k] ?? '#cbd5e1', flexShrink: 0 }} />
                        <Text size="xs" c="dimmed" style={{ minWidth: 70 }}>{k}</Text>
                        <Text size="xs" fw={600} c="dark">{v as number}</Text>
                      </Group>
                    ))}
                </Stack>
              </Group>
            ) : <Skeleton height={150} />}
          </Box>
        </Grid.Col>

        {/* Category chart */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Box style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '16px' : '20px 24px', border: '1px solid #e2e8f0' }}>
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>{t("dashboard.byCategory")}</Text>
            {assetData ? (
              <BarChart
                h={isMobile ? 160 : 180}
                data={categoryData}
                dataKey="name"
                series={[{ name: 'value', label: 'Count', color: '#6366f1' }]}
                tickLine="none"
                gridAxis="y"
                orientation={isMobile ? 'horizontal' : 'vertical'}
              />
            ) : <Skeleton height={isMobile ? 160 : 180} />}
          </Box>
        </Grid.Col>

        {/* License expiring */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '16px' : '20px 24px', border: '1px solid #e2e8f0' }}>
            <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>{t("dashboard.upcomingRenewals","Upcoming Renewals")}</Text>
            {licSummary ? (
              licSummary.upcomingRenewals?.length > 0 ? (
                <Stack gap={8}>
                  {licSummary.upcomingRenewals.map((l: any) => (
                    <Box key={l.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#f8fafc', borderRadius: 7 }}>
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} lineClamp={1}>{l.productName}</Text>
                        <Text size="xs" c="dimmed">{l.expiryDate?.slice(0,10)}</Text>
                      </Box>
                      <Badge size="xs" color={l.daysUntilExpiry <= 7 ? 'red' : 'orange'} variant="light" ml={8}>
                        {l.daysUntilExpiry}d
                      </Badge>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">{t("dashboard.noUpcomingRenewals","No upcoming renewals")}</Text>
              )
            ) : <Skeleton height={120} />}
          </Box>
        </Grid.Col>

        {/* ── Borrow Overdue Alert ── */}
        {overdueData && overdueData.borrows > 0 && (
          <Grid.Col span={12}>
            <Box style={{ background: '#fef2f2', borderRadius: 10, padding: '14px 18px', border: '1.5px solid #fca5a5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <Group gap={10}>
                <IconClock size={20} color="#dc2626" />
                <Box>
                  <Text size="sm" fw={700} c="red">{t("dashboard.borrowOverdue","Borrow Overdue")} — {overdueData.borrows} {t('dashboard.requestsPastDue','request(s) past due date')}</Text>
                  <Text size="xs" c="dimmed">Assets have not been returned after the due date</Text>
                </Box>
              </Group>
              <Anchor size="xs" c="red" onClick={() => navigate('/borrow')}>{t("dashboard.viewBorrows","View Borrows →")}</Anchor>
            </Box>
          </Grid.Col>
        )}

        {/* ── Activity Feed ── */}
        <Grid.Col span={12}>
          <Box style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '16px' : '20px 24px', border: '1px solid #e2e8f0' }}>
            <Group justify="space-between" mb={12}>
              <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t("dashboard.recentActivity")}</Text>
              <Text size="xs" c="dimmed">{t("common.lastActions","Last {{count}} actions", { count: 15 })}</Text>
            </Group>
            {activity.length === 0 ? (
              <Text size="sm" c="dimmed">{t("dashboard.noRecentActivity","No recent activity")}</Text>
            ) : (
              <ScrollArea h={220}>
                <Stack gap={6}>
                  {activity.map((m: any) => {
                    const typeColor: Record<string, string> = {
                      OWNER_CHANGE: 'blue', LOCATION_CHANGE: 'teal', STATUS_CHANGE: 'orange',
                      BORROW: 'violet', RETURN: 'green', DEPT_TRANSFER: 'indigo',
                      ASSET_EDIT: 'gray', ASSET_SWAP: 'pink', LOCATION_MOVE: 'cyan',
                    }
                    const typeLabel: Record<string, string> = {
                      OWNER_CHANGE: 'Owner changed', LOCATION_CHANGE: 'Moved',
                      STATUS_CHANGE: t('common.statusChanged','Status changed'), BORROW: t('common.borrowed','Borrowed'),
                      RETURN: 'Returned', DEPT_TRANSFER: 'Transferred',
                      ASSET_EDIT: 'Edited', ASSET_SWAP: 'Swapped', LOCATION_MOVE: 'Location moved',
                    }
                    const dt = new Date(m.createdAt)
                    const timeStr = dt.toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })
                    return (
                      <Box key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
                        <Badge size="xs" color={typeColor[m.type] ?? 'gray'} variant="light" style={{ flexShrink: 0, marginTop: 2 }}>
                          {typeLabel[m.type] ?? m.type}
                        </Badge>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="xs" fw={600} lineClamp={1}>
                            {m.asset?.assetCode ?? '—'} {m.asset?.name ? `— ${m.asset.name}` : ''}
                          </Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {m.fromValue && m.toValue ? `${m.fromValue} → ${m.toValue}` : (m.note ?? '')}
                          </Text>
                        </Box>
                        <Box style={{ flexShrink: 0, textAlign: 'right' }}>
                          <Text size="xs" c="dimmed">{timeStr}</Text>
                          <Text size="xs" c="dimmed">{m.performedBy ?? '—'}</Text>
                        </Box>
                      </Box>
                    )
                  })}
                </Stack>
              </ScrollArea>
            )}
          </Box>
        </Grid.Col>
      </Grid>
    </Box>
  )
}
